import React, { useState, useEffect, useMemo } from 'react';
import { Product } from '../types';
import { showToast, formatMoney } from '../utils';

interface CalendarViewProps {
  active: boolean;
  products: Product[];
}

export interface CalendarMilestone {
  day: number;
  type: 'launch' | 'payout' | 'holiday' | 'fba' | 'ads';
  label: string;
  multiplier?: number; // Traffic/sales multiplier on that day
}

export interface MonthlyPlanItem {
  id: string; // YYYY-MM
  year: number;
  monthZeroIndexed: number; // 0..11
  monthIndex: number; // 1..12
  monthLabel: string;
  shortLabel: string;
  seasonTag: string;
  phaseTitle: string;
  whatToExpect: string;
  activeSkusGoal: number;
  fbaHeroSkusGoal: number;
  expectedDailyOrders: number;
  expectedMonthlyOrders: number;
  expectedGrossRevenue: number;
  expectedAdSpend: number;
  targetAcosPct: number;
  targetTacosPct: number;
  organicVsAdSplit: string;
  expectedNetProfit: number;
  expectedNetMarginPct: number;
  milestones: CalendarMilestone[];
  keyFocusChecklist: string[];
  kpisToWatch: {
    buyBoxTarget: string;
    odrTarget: string;
    validTrackingTarget: string;
    returnRateTarget: string;
  };
}

export interface DailyActualEntry {
  orders: number;
  revenue: number;
  impressions?: number;
  clicks?: number;
  adSpend: number;
  netProfit: number;
  note?: string;
}

export interface MonthlyActualRecord {
  actualOrders: number;
  actualRevenue: number;
  actualAdSpend: number;
  actualNetProfit: number;
  completedTasks: number[];
  notes: string;
  dailyLogs?: Record<number, DailyActualEntry>;
}

const TWELVE_MONTH_ROADMAP: MonthlyPlanItem[] = [
  {
    id: '2026-09',
    year: 2026,
    monthZeroIndexed: 8,
    monthIndex: 0,
    monthLabel: 'September 2026',
    shortLabel: 'SEP 26',
    seasonTag: 'PRE-LAUNCH · SOURCING & STORE SETUP',
    phaseTitle: 'Supplier Vetting, CJ US Warehouse & SP-API Connection',
    whatToExpect:
      'Foundation & pre-launch month: connecting Amazon Seller Central (BackdropsForLess) via SP-API, linking CJ Dropshipping US domestic warehouses, vetting initial 64-SKU test batch, and verifying automated routing before full 206-SKU catalog expansion and Sponsored Products launch in October.',
    activeSkusGoal: 64,
    fbaHeroSkusGoal: 0,
    expectedDailyOrders: 0.5,
    expectedMonthlyOrders: 15,
    expectedGrossRevenue: 535,
    expectedAdSpend: 0,
    targetAcosPct: 0.0,
    targetTacosPct: 0.0,
    organicVsAdSplit: '100% Organic / $0 Ads',
    expectedNetProfit: 292,
    expectedNetMarginPct: 54.6,
    milestones: [
      { day: 7, type: 'holiday', label: '🇺🇸 Labor Day Catalog Prep' },
      { day: 15, type: 'launch', label: '🔗 Amazon SP-API Connected' },
      { day: 22, type: 'launch', label: '🏭 CJ US Warehouse Linked' },
      { day: 29, type: 'launch', label: '📦 Initial 64 SKUs Staged' }
    ],
    keyFocusChecklist: [
      'Complete Amazon SP-API LWA OAuth connection for BackdropsForLess (A3R7XJXJW7FKHB)',
      'Connect CJ Dropshipping API v2.0 for 2–5 day US domestic warehouse fulfillment',
      'Establish $35.55 average retail pricing & 54.6% gross margin baseline before ad spend',
      'Prepare 206-SKU catalog feed & GTIN exemption /Generic brand compliance rules'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 80%',
      odrTarget: '< 0.5%',
      validTrackingTarget: '≥ 98%',
      returnRateTarget: '< 3.0%'
    }
  },
  {
    id: '2026-10',
    year: 2026,
    monthZeroIndexed: 9,
    monthIndex: 1,
    monthLabel: 'October 2026',
    shortLabel: 'OCT 26',
    seasonTag: 'MONTH 01 · LAUNCH & INDEXING',
    phaseTitle: 'Catalog Stabilization & Initial Ad Indexing',
    whatToExpect:
      'Amazon indexes your 206 listings and Sponsored Products Auto campaign (#38279571054794). Expect impressions to ramp during Weeks 1–2 while Amazon tests search placements at $0.23–$0.30 CPC. First consistent daily orders come from long-tail cleaning brushes, shelves, and electric kettles.',
    activeSkusGoal: 206,
    fbaHeroSkusGoal: 0,
    expectedDailyOrders: 2.5,
    expectedMonthlyOrders: 75,
    expectedGrossRevenue: 2665,
    expectedAdSpend: 195,
    targetAcosPct: 18.0,
    targetTacosPct: 7.3,
    organicVsAdSplit: '30% Organic / 70% Ad',
    expectedNetProfit: 1260,
    expectedNetMarginPct: 47.3,
    milestones: [
      { day: 8, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 13, type: 'holiday', label: '🔥 Prime Big Deal Days', multiplier: 1.8 },
      { day: 21, type: 'launch', label: '🚀 206-SKU Catalog Synced' },
      { day: 22, type: 'ads', label: '⚡ Ads Campaign #38279571054794 Live' },
      { day: 23, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 26, type: 'ads', label: '🔍 Week-1 Ad Impression Audit' },
      { day: 31, type: 'holiday', label: '🎃 Halloween / Oct Closeout' }
    ],
    keyFocusChecklist: [
      'Resolve remaining 34 Rejected/Attention listings so all 206 SKUs are Active · Buyable',
      'Add newly buyable ASINs into Campaign #38279571054794 once offer indexing finishes',
      'Verify CJ Dropshipping US Warehouse tracking uploads within 24–48h of each order',
      'Request Amazon "Request a Review" automation on all delivered October orders'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 88%',
      odrTarget: '< 0.5%',
      validTrackingTarget: '≥ 98%',
      returnRateTarget: '< 3.0%'
    }
  },
  {
    id: '2026-11',
    year: 2026,
    monthZeroIndexed: 10,
    monthIndex: 2,
    monthLabel: 'November 2026',
    shortLabel: 'NOV 26',
    seasonTag: 'MONTH 02 · BLACK FRIDAY / CYBER MONDAY',
    phaseTitle: 'Q4 Traffic Surge & Winner Identification',
    whatToExpect:
      'Shopper conversion rates jump 35–50% starting Nov 15 through Black Friday / Cyber Monday. Massagers, vanity trays, and electric tea kettles see strong early holiday gifting demand. Your top 10–15 SKUs will emerge clearly by Thanksgiving.',
    activeSkusGoal: 206,
    fbaHeroSkusGoal: 3,
    expectedDailyOrders: 5.5,
    expectedMonthlyOrders: 165,
    expectedGrossRevenue: 5865,
    expectedAdSpend: 320,
    targetAcosPct: 16.5,
    targetTacosPct: 5.5,
    organicVsAdSplit: '40% Organic / 60% Ad',
    expectedNetProfit: 2880,
    expectedNetMarginPct: 49.1,
    milestones: [
      { day: 5, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 10, type: 'ads', label: '⚡ Search Term Harvest & Negatives' },
      { day: 19, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 20, type: 'holiday', label: '🔥 Black Friday Early Deals Start', multiplier: 1.5 },
      { day: 26, type: 'holiday', label: '🦃 Thanksgiving Day' },
      { day: 27, type: 'holiday', label: '🚀 BLACK FRIDAY PEAK (3x Sales)', multiplier: 3.0 },
      { day: 30, type: 'holiday', label: '⚡ CYBER MONDAY PEAK (2.8x Sales)', multiplier: 2.8 }
    ],
    keyFocusChecklist: [
      'Download Search Term Report on Nov 10; move converting search terms to Exact/Phrase keywords',
      'Add negative keywords for any search term with >12 clicks and 0 orders',
      'Raise daily budget cap from $25/day to $35/day during Black Friday week (Nov 23–30)',
      'Identify top 3 best-selling US Dropship SKUs (>15 sales/mo) for Alibaba FBA test batch'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 92%',
      odrTarget: '< 0.5%',
      validTrackingTarget: '≥ 98%',
      returnRateTarget: '< 3.2%'
    }
  },
  {
    id: '2026-12',
    year: 2026,
    monthZeroIndexed: 11,
    monthIndex: 3,
    monthLabel: 'December 2026',
    shortLabel: 'DEC 26',
    seasonTag: 'MONTH 03 · HOLIDAY GIFTING PEAK',
    phaseTitle: 'Peak Q4 Velocity & Organic Rank Compounding',
    whatToExpect:
      'Dec 1–18 is the highest-converting shopping window of the year on Amazon US. Products with 3+ customer reviews convert at 2x the baseline rate. After Dec 20, gifting slows and transitions into New Year home organization & cleaning demand.',
    activeSkusGoal: 206,
    fbaHeroSkusGoal: 5,
    expectedDailyOrders: 8.5,
    expectedMonthlyOrders: 260,
    expectedGrossRevenue: 9240,
    expectedAdSpend: 450,
    targetAcosPct: 15.5,
    targetTacosPct: 4.9,
    organicVsAdSplit: '50% Organic / 50% Ad',
    expectedNetProfit: 4600,
    expectedNetMarginPct: 49.8,
    milestones: [
      { day: 3, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 14, type: 'holiday', label: '🔥 Green Monday Gifting Peak', multiplier: 2.2 },
      { day: 17, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 18, type: 'fba', label: '📦 Place First 5-SKU Alibaba FBA Order' },
      { day: 21, type: 'ads', label: '⚡ Pivot Bids to Storage & Cleaning' },
      { day: 26, type: 'holiday', label: '🎁 Post-Holiday Gift Card Spend', multiplier: 1.4 },
      { day: 31, type: 'payout', label: '💰 Year-End Payout & Tax Ledger' }
    ],
    keyFocusChecklist: [
      'Maximize in-stock quantity on top 25 giftable SKUs (massagers, crystal shelves, glass kettles)',
      'Pivot ad bids on Dec 21 toward storage racks, closet shelves, and deep-cleaning scrubbers',
      'Place first 150-unit Alibaba FBA Prime order for the top 5 proven SKUs using Q4 profits',
      'Audit Year-End 1099-K / COGS ledger for tax deduction optimization'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 94%',
      odrTarget: '< 0.5%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 4.0%'
    }
  },
  {
    id: '2027-01',
    year: 2027,
    monthZeroIndexed: 0,
    monthIndex: 4,
    monthLabel: 'January 2027',
    shortLabel: 'JAN 27',
    seasonTag: 'MONTH 04 · NEW YEAR ORGANIZATION BOOM',
    phaseTitle: 'Home Organization, Shelving & Deep-Clean Surge',
    whatToExpect:
      'While gift categories cool down in January, your catalog is heavily weighted toward Bathroom/Kitchen Shelving (66 SKUs), Cleaning Brushes (64 SKUs), and Fitness/Recovery Massagers (49 SKUs)—all of which peak in January for New Year resolutions and home organization.',
    activeSkusGoal: 220,
    fbaHeroSkusGoal: 8,
    expectedDailyOrders: 9.5,
    expectedMonthlyOrders: 295,
    expectedGrossRevenue: 10485,
    expectedAdSpend: 480,
    targetAcosPct: 15.0,
    targetTacosPct: 4.6,
    organicVsAdSplit: '55% Organic / 45% Ad',
    expectedNetProfit: 5250,
    expectedNetMarginPct: 50.1,
    milestones: [
      { day: 1, type: 'holiday', label: '✨ New Year Home Organization Surge', multiplier: 1.7 },
      { day: 11, type: 'fba', label: '📦 First 5 FBA Hero SKUs Check Into ONT8' },
      { day: 14, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 20, type: 'launch', label: '🚀 Expand Catalog to 220 Active SKUs' },
      { day: 28, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' }
    ],
    keyFocusChecklist: [
      'First 5 Alibaba FBA Prime batches check into ONT8 warehouse (Prime badge boosts CVR +65%)',
      'Add 15 new storage & closet organization SKUs from CJ US Warehouse',
      'Process post-holiday returns promptly to protect Account Health rating',
      'Lower bids by $0.04 on seasonal gift items; boost bids +$0.05 on closet & garage shelves'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 94%',
      odrTarget: '< 0.5%',
      validTrackingTarget: '≥ 98%',
      returnRateTarget: '< 4.2%'
    }
  },
  {
    id: '2027-02',
    year: 2027,
    monthZeroIndexed: 1,
    monthIndex: 5,
    monthLabel: 'February 2027',
    shortLabel: 'FEB 27',
    seasonTag: 'MONTH 05 · FBA PRIME MARGIN EXPANSION',
    phaseTitle: 'Prime Badge Velocity & Valentine Self-Care',
    whatToExpect:
      'Shortest month of the year, stabilized by Valentine’s Day beauty/wellness gifting (facial sculpting wands, neck massagers, vanity trays) and your first FBA Prime hero SKUs winning top-of-page organic placements.',
    activeSkusGoal: 230,
    fbaHeroSkusGoal: 12,
    expectedDailyOrders: 12.0,
    expectedMonthlyOrders: 336,
    expectedGrossRevenue: 11945,
    expectedAdSpend: 510,
    targetAcosPct: 14.5,
    targetTacosPct: 4.3,
    organicVsAdSplit: '58% Organic / 42% Ad',
    expectedNetProfit: 6120,
    expectedNetMarginPct: 51.2,
    milestones: [
      { day: 4, type: 'fba', label: '📦 Pre-CNY Alibaba Q2 Restock Order' },
      { day: 11, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 14, type: 'holiday', label: '💝 Valentine’s Beauty & Wellness Peak', multiplier: 1.6 },
      { day: 15, type: 'holiday', label: '🇺🇸 Presidents’ Day Home Sale', multiplier: 1.5 },
      { day: 25, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' }
    ],
    keyFocusChecklist: [
      'Pre-book Q2 Alibaba replenishment before Chinese New Year factory pause concludes',
      'Launch Sponsored Products Product Targeting ads on competitor ASINs priced $5+ higher',
      'Expand top 10 winning Amazon SKUs to Walmart WFS & Shopify DTC channels'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 95%',
      odrTarget: '< 0.4%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 3.2%'
    }
  },
  {
    id: '2027-03',
    year: 2027,
    monthZeroIndexed: 2,
    monthIndex: 6,
    monthLabel: 'March 2027',
    shortLabel: 'MAR 27',
    seasonTag: 'MONTH 06 · SPRING CLEANING PEAK',
    phaseTitle: 'Spring Cleaning & Tax-Refund Shopping Wave',
    whatToExpect:
      'March kicks off peak Spring Cleaning season across the US. Your 64 Cleaning Brush & Electric Spin Scrubber SKUs experience their highest organic search volume of the first half of the year, aided by US tax-refund consumer spending.',
    activeSkusGoal: 250,
    fbaHeroSkusGoal: 16,
    expectedDailyOrders: 15.5,
    expectedMonthlyOrders: 480,
    expectedGrossRevenue: 17065,
    expectedAdSpend: 680,
    targetAcosPct: 14.0,
    targetTacosPct: 4.0,
    organicVsAdSplit: '62% Organic / 38% Ad',
    expectedNetProfit: 8890,
    expectedNetMarginPct: 52.1,
    milestones: [
      { day: 11, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 15, type: 'ads', label: '⚡ Boost Cleaning Brush Ad Budget +35%' },
      { day: 20, type: 'holiday', label: '🌸 First Day of Spring Cleaning Surge', multiplier: 1.7 },
      { day: 25, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' }
    ],
    keyFocusChecklist: [
      'Boost Ad Group 01 (Cleaning Brushes & Spin Scrubbers) daily budget by 35%',
      'Bundle replacement brush heads & microfiber pads for higher Average Order Value ($44+)',
      'Cross-list 250 active SKUs across TikTok Shop US for viral cleaning demos'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 95%',
      odrTarget: '< 0.4%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.9%'
    }
  },
  {
    id: '2027-04',
    year: 2027,
    monthZeroIndexed: 3,
    monthIndex: 7,
    monthLabel: 'April 2027',
    shortLabel: 'APR 27',
    seasonTag: 'MONTH 07 · HOME REFRESH & PATIO PREP',
    phaseTitle: 'Garage, Pool, Window & Outdoor Scrubbers',
    whatToExpect:
      'Homeowners shift from indoor cleaning to garage shelving, window track brushes, pool tile brushes, and patio organization. High-ticket heavy-duty garage racks ($69–$89) drive up average order value and net profit per order.',
    activeSkusGoal: 265,
    fbaHeroSkusGoal: 20,
    expectedDailyOrders: 18.0,
    expectedMonthlyOrders: 540,
    expectedGrossRevenue: 19195,
    expectedAdSpend: 740,
    targetAcosPct: 13.8,
    targetTacosPct: 3.9,
    organicVsAdSplit: '65% Organic / 35% Ad',
    expectedNetProfit: 10120,
    expectedNetMarginPct: 52.7,
    milestones: [
      { day: 8, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 15, type: 'fba', label: '📦 20 Hero SKUs Active in FBA Prime' },
      { day: 22, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 28, type: 'ads', label: '⚡ Enable 5% Green Clippable Coupons' }
    ],
    keyFocusChecklist: [
      'Scale top 20 proven SKUs into FBA Prime (generating ~65% of total store profit)',
      'Enable 5% green clippable coupons on hero listings to boost search click-through rate',
      'Prune any zero-impression SKUs older than 90 days and replace with fresh CJ US items'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 96%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.8%'
    }
  },
  {
    id: '2027-05',
    year: 2027,
    monthZeroIndexed: 4,
    monthIndex: 8,
    monthLabel: 'May 2027',
    shortLabel: 'MAY 27',
    seasonTag: 'MONTH 08 · MOTHER’S DAY & MEMORIAL DAY',
    phaseTitle: 'Mother’s Day Wellness & Moving Season',
    whatToExpect:
      'First two weeks of May bring a major Mother’s Day spike for facial massagers, foot rollers, tea kettles, and vanity organizers. Late May marks the start of US summer moving season (high demand for bathroom caddies & kitchen racks).',
    activeSkusGoal: 280,
    fbaHeroSkusGoal: 25,
    expectedDailyOrders: 21.5,
    expectedMonthlyOrders: 665,
    expectedGrossRevenue: 23640,
    expectedAdSpend: 890,
    targetAcosPct: 13.5,
    targetTacosPct: 3.8,
    organicVsAdSplit: '67% Organic / 33% Ad',
    expectedNetProfit: 12550,
    expectedNetMarginPct: 53.1,
    milestones: [
      { day: 6, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 9, type: 'holiday', label: '💐 Mother’s Day Gifting Peak', multiplier: 2.0 },
      { day: 15, type: 'fba', label: '📦 Prime Day Deal Submission Cutoff' },
      { day: 20, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 31, type: 'holiday', label: '🇺🇸 Memorial Day Weekend Sale', multiplier: 1.8 }
    ],
    keyFocusChecklist: [
      'Submit Prime Day lightning deal / Prime Exclusive Discount nominations before May 15 deadline',
      'Ship Prime Day FBA inventory batch to Amazon fulfillment centers before June cutoff',
      'Run Mother’s Day gift headline promotion across Shopify & Amazon top 15 beauty SKUs'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 96%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.7%'
    }
  },
  {
    id: '2027-06',
    year: 2027,
    monthZeroIndexed: 5,
    monthIndex: 9,
    monthLabel: 'June 2027',
    shortLabel: 'JUN 27',
    seasonTag: 'MONTH 09 · FATHER’S DAY & APARTMENT MOVES',
    phaseTitle: 'Peak Apartment Turnover & Pre-Prime Day Warmup',
    whatToExpect:
      'June is peak US lease-turnover and home-remodeling month—shower niches, adhesive corner caddies, spice racks, and smart home bulb cameras sell steadily every day. Father’s Day lifts deep-tissue massage guns and garage tool shelves.',
    activeSkusGoal: 300,
    fbaHeroSkusGoal: 30,
    expectedDailyOrders: 25.0,
    expectedMonthlyOrders: 750,
    expectedGrossRevenue: 26660,
    expectedAdSpend: 980,
    targetAcosPct: 13.2,
    targetTacosPct: 3.7,
    organicVsAdSplit: '68% Organic / 32% Ad',
    expectedNetProfit: 14280,
    expectedNetMarginPct: 53.6,
    milestones: [
      { day: 3, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 15, type: 'fba', label: '📦 Prime Day FBA Inventory Check-In Cutoff' },
      { day: 17, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 20, type: 'holiday', label: '👔 Father’s Day Peak (Garage & Massagers)', multiplier: 1.7 }
    ],
    keyFocusChecklist: [
      'Confirm all 30 FBA Hero SKUs are checked in and FC-available ahead of July Prime Day',
      'Verify A+ Content / Brand Story graphics on top 30 listings',
      'Audit CJ US Warehouse stock levels for summer travel kettles & smart cameras'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 96%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.7%'
    }
  },
  {
    id: '2027-07',
    year: 2027,
    monthZeroIndexed: 6,
    monthIndex: 10,
    monthLabel: 'July 2027',
    shortLabel: 'JUL 27',
    seasonTag: 'MONTH 10 · AMAZON PRIME DAY EVENT',
    phaseTitle: 'Prime Day Velocity Spike & Rank Lock-In',
    whatToExpect:
      'Amazon Prime Day (mid-July) delivers a 48-hour traffic explosion equal to 2 weeks of normal sales. Even non-deal items benefit from massive site-wide shopper traffic, locking in page-1 organic BSR rankings for the rest of the summer.',
    activeSkusGoal: 315,
    fbaHeroSkusGoal: 35,
    expectedDailyOrders: 34.0,
    expectedMonthlyOrders: 1050,
    expectedGrossRevenue: 37325,
    expectedAdSpend: 1420,
    targetAcosPct: 14.0,
    targetTacosPct: 3.8,
    organicVsAdSplit: '66% Organic / 34% Ad',
    expectedNetProfit: 19850,
    expectedNetMarginPct: 53.2,
    milestones: [
      { day: 1, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 4, type: 'holiday', label: '🎆 July 4th Home & Patio Sale', multiplier: 1.5 },
      { day: 13, type: 'holiday', label: '🚀 AMAZON PRIME DAY 1 (4x Traffic)', multiplier: 3.5 },
      { day: 14, type: 'holiday', label: '🚀 AMAZON PRIME DAY 2 (4x Traffic)', multiplier: 3.5 },
      { day: 15, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 29, type: 'payout', label: '💰 Post-Prime Day Mega Payout' }
    ],
    keyFocusChecklist: [
      'Double daily ad budget cap during the 48-hour Prime Day window to prevent afternoon budget exhaustion',
      'Activate 15% Prime Exclusive Discounts on top 20 high-margin FBA SKUs',
      'Harvest new high-converting Prime Day search terms into permanent Exact Match campaigns'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 97%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 3.0%'
    }
  },
  {
    id: '2027-08',
    year: 2027,
    monthZeroIndexed: 7,
    monthIndex: 11,
    monthLabel: 'August 2027',
    shortLabel: 'AUG 27',
    seasonTag: 'MONTH 11 · BACK TO COLLEGE & DORM RUSH',
    phaseTitle: 'College Dorm Storage, Kettles & Caddy Surge',
    whatToExpect:
      'August is the #1 month of the year for shower caddies, over-the-door organizers, electric pour-over/ramen kettles, and desk monitor risers as millions of students outfit dorms and off-campus apartments.',
    activeSkusGoal: 330,
    fbaHeroSkusGoal: 40,
    expectedDailyOrders: 36.5,
    expectedMonthlyOrders: 1130,
    expectedGrossRevenue: 40170,
    expectedAdSpend: 1450,
    targetAcosPct: 13.0,
    targetTacosPct: 3.6,
    organicVsAdSplit: '70% Organic / 30% Ad',
    expectedNetProfit: 21680,
    expectedNetMarginPct: 54.0,
    milestones: [
      { day: 12, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 15, type: 'holiday', label: '🎓 Back-to-College Dorm Move-In Peak', multiplier: 2.0 },
      { day: 20, type: 'fba', label: '📦 Place Year-2 Q4 Holiday Alibaba Order' },
      { day: 26, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' }
    ],
    keyFocusChecklist: [
      'Emphasize "Dorm Essential / No-Drill Apartment Safe" in bullet #1 of shelf & kettle SKUs',
      'Place Q4 Holiday Alibaba sea-freight FBA order by Aug 20 so stock lands before Oct 25',
      'Review 11-month SKU profitability and retire bottom 5% underperformers'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 97%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.6%'
    }
  },
  {
    id: '2027-09',
    year: 2027,
    monthZeroIndexed: 8,
    monthIndex: 12,
    monthLabel: 'September 2027',
    shortLabel: 'SEP 27',
    seasonTag: 'MONTH 12 · FALL ROUTINE & Q4 STAGING',
    phaseTitle: 'Year-1 Milestone ($40K+/mo Run Rate) & Q4 Prep',
    whatToExpect:
      'By Month 12, your catalog has 12 months of accumulated reviews, organic keyword authority, and 45+ FBA Prime hero SKUs supplemented by 300+ zero-inventory US Dropship listings—positioning you to 2.5x volume entering your second Q4.',
    activeSkusGoal: 350,
    fbaHeroSkusGoal: 45,
    expectedDailyOrders: 39.0,
    expectedMonthlyOrders: 1170,
    expectedGrossRevenue: 41595,
    expectedAdSpend: 1480,
    targetAcosPct: 12.8,
    targetTacosPct: 3.6,
    organicVsAdSplit: '72% Organic / 28% Ad',
    expectedNetProfit: 22620,
    expectedNetMarginPct: 54.4,
    milestones: [
      { day: 6, type: 'holiday', label: '🇺🇸 Labor Day Fall Home Sale', multiplier: 1.7 },
      { day: 9, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 23, type: 'payout', label: '💰 Amazon Bi-Weekly Payout' },
      { day: 30, type: 'launch', label: '🏆 Year-1 P&L Audit ($245K+ Annual Milestone)' }
    ],
    keyFocusChecklist: [
      'Complete Year-1 P&L audit across Amazon US, Shopify DTC, Walmart WFS, and TikTok Shop',
      'Check in Year-2 Q4 holiday FBA inventory ahead of Amazon October Prime Big Deal Days',
      'Lock in 5–8% unit cost reduction with top Alibaba suppliers based on Year-1 volume'
    ],
    kpisToWatch: {
      buyBoxTarget: '≥ 97%',
      odrTarget: '< 0.3%',
      validTrackingTarget: '≥ 99%',
      returnRateTarget: '< 2.5%'
    }
  }
];

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const STORAGE_KEY = 'aurelius_business_calendar_actuals_v4';

export const CalendarView: React.FC<CalendarViewProps> = ({ active, products }) => {
  const [selectedMonthId, setSelectedMonthId] = useState<string>('2026-10');
  const [selectedDay, setSelectedDay] = useState<number>(24);
  const [actualsByMonth, setActualsByMonth] = useState<Record<string, MonthlyActualRecord>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return {
      '2026-10': {
        actualOrders: 0,
        actualRevenue: 0,
        actualAdSpend: 0,
        actualNetProfit: 0,
        completedTasks: [1, 2],
        notes: 'Live Amazon SP-API Orders (/orders/v0/orders) connected · Sponsored Products Campaign #38279571054794 live with 170 advertised SKUs at $0.40 default bid (47 live impressions).',
        dailyLogs: {
          22: { orders: 0, revenue: 0, impressions: 18, clicks: 0, adSpend: 0, netProfit: 0 },
          23: { orders: 0, revenue: 0, impressions: 29, clicks: 0, adSpend: 0, netProfit: 0 },
          24: { orders: 0, revenue: 0, impressions: 47, clicks: 0, adSpend: 0, netProfit: 0 }
        }
      }
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(actualsByMonth));
    } catch (_) {}
  }, [actualsByMonth]);

  // Automatically sync real customer orders from Amazon SP-API (/api/amazon/orders)
  useEffect(() => {
    if (!active) return;
    fetch('/api/amazon/orders')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.ok || !Array.isArray(data.orders)) return;
        const validOrders = data.orders.filter((o: Record<string, unknown>) => o.OrderStatus !== 'Canceled');
        if (!validOrders.length) return;

        setActualsByMonth((prev) => {
          const next = { ...prev };
          const byMonth: Record<string, { orders: number; rev: number; daily: Record<number, DailyActualEntry> }> = {};

          for (const ord of validOrders) {
            const dt = new Date(String(ord.PurchaseDate || new Date().toISOString()));
            const ym = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
            const day = dt.getUTCDate();
            const amt = Number((ord.OrderTotal as { Amount?: string })?.Amount || 35.55);
            if (!byMonth[ym]) byMonth[ym] = { orders: 0, rev: 0, daily: {} };
            byMonth[ym].orders += 1;
            byMonth[ym].rev = +(byMonth[ym].rev + amt).toFixed(2);
            const curDay = byMonth[ym].daily[day] || { orders: 0, revenue: 0, adSpend: 0, netProfit: 0 };
            curDay.orders += 1;
            curDay.revenue = +(curDay.revenue + amt).toFixed(2);
            curDay.netProfit = +(curDay.revenue * 0.45).toFixed(2);
            byMonth[ym].daily[day] = curDay;
          }

          for (const [ym, summary] of Object.entries(byMonth)) {
            const existing = next[ym] || {
              actualOrders: 0,
              actualRevenue: 0,
              actualAdSpend: 0,
              actualNetProfit: 0,
              completedTasks: [],
              notes: '',
              dailyLogs: {}
            };
            next[ym] = {
              ...existing,
              actualOrders: summary.orders,
              actualRevenue: summary.rev,
              actualNetProfit: +(summary.rev * 0.546 - (existing.actualAdSpend || 0)).toFixed(2),
              dailyLogs: summary.daily
            };
          }
          return next;
        });
      })
      .catch(() => {});
  }, [active]);

  const selectedMonthIdx = useMemo(() => {
    const idx = TWELVE_MONTH_ROADMAP.findIndex((m) => m.id === selectedMonthId);
    return idx >= 0 ? idx : 1;
  }, [selectedMonthId]);

  const selectedMonth = TWELVE_MONTH_ROADMAP[selectedMonthIdx] || TWELVE_MONTH_ROADMAP[1];
  const prevMonth = selectedMonthIdx > 0 ? TWELVE_MONTH_ROADMAP[selectedMonthIdx - 1] : null;
  const nextMonth =
    selectedMonthIdx < TWELVE_MONTH_ROADMAP.length - 1 ? TWELVE_MONTH_ROADMAP[selectedMonthIdx + 1] : null;

  const [customComparePrevId, setCustomComparePrevId] = useState<string>('');
  const [customCompareNextId, setCustomCompareNextId] = useState<string>('');

  const comparedPrevMonth = useMemo(() => {
    if (customComparePrevId) {
      const found = TWELVE_MONTH_ROADMAP.find((m) => m.id === customComparePrevId);
      if (found) return found;
    }
    return prevMonth || TWELVE_MONTH_ROADMAP[0];
  }, [customComparePrevId, prevMonth]);

  const comparedNextMonth = useMemo(() => {
    if (customCompareNextId) {
      const found = TWELVE_MONTH_ROADMAP.find((m) => m.id === customCompareNextId);
      if (found) return found;
    }
    return nextMonth || TWELVE_MONTH_ROADMAP[TWELVE_MONTH_ROADMAP.length - 1];
  }, [customCompareNextId, nextMonth]);

  const selectMonthWithReset = (monthId: string, dayToSelect = 1) => {
    setSelectedMonthId(monthId);
    setSelectedDay(dayToSelect);
    setCustomComparePrevId('');
    setCustomCompareNextId('');
  };

  const emptyActualRecord: MonthlyActualRecord = {
    actualOrders: 0,
    actualRevenue: 0,
    actualAdSpend: 0,
    actualNetProfit: 0,
    completedTasks: [],
    notes: '',
    dailyLogs: {}
  };

  const currentActual: MonthlyActualRecord = actualsByMonth[selectedMonth.id] || emptyActualRecord;
  const comparedPrevActual: MonthlyActualRecord = actualsByMonth[comparedPrevMonth.id] || emptyActualRecord;
  const comparedNextActual: MonthlyActualRecord = actualsByMonth[comparedNextMonth.id] || emptyActualRecord;

  // Build calendar cells for selectedMonth
  const calendarDays = useMemo(() => {
    const firstDayOfWeek = new Date(selectedMonth.year, selectedMonth.monthZeroIndexed, 1).getDay();
    const daysInMonth = new Date(selectedMonth.year, selectedMonth.monthZeroIndexed + 1, 0).getDate();
    const cells: Array<{
      day: number | null;
      weekdayIndex: number;
      expectedOrders: number;
      expectedRev: number;
      expectedImpressions: number;
      expectedClicks: number;
      expectedAdSpend: number;
      milestone?: CalendarMilestone;
      isWeeklyAdAudit: boolean;
      dailyActual?: DailyActualEntry;
    }> = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push({
        day: null,
        weekdayIndex: i,
        expectedOrders: 0,
        expectedRev: 0,
        expectedImpressions: 0,
        expectedClicks: 0,
        expectedAdSpend: 0,
        isWeeklyAdAudit: false
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const weekdayIndex = (firstDayOfWeek + d - 1) % 7;
      const milestone = selectedMonth.milestones.find((m) => m.day === d);
      const mult = milestone?.multiplier || (weekdayIndex === 0 || weekdayIndex === 6 ? 1.15 : 1.0);
      const expectedOrders = +(selectedMonth.expectedDailyOrders * mult).toFixed(1);
      const expectedRev = Math.round(expectedOrders * 35.55);
      const expectedAdSpend =
        selectedMonth.expectedAdSpend > 0 ? +((selectedMonth.expectedAdSpend / daysInMonth) * mult).toFixed(2) : 0;
      const expectedClicks =
        selectedMonth.expectedAdSpend > 0 ? Math.max(1, Math.round(expectedAdSpend / 0.32)) : 0;
      const expectedImpressions =
        selectedMonth.expectedAdSpend > 0 ? Math.round(expectedClicks * 145) : 0;
      cells.push({
        day: d,
        weekdayIndex,
        expectedOrders,
        expectedRev,
        expectedImpressions,
        expectedClicks,
        expectedAdSpend,
        milestone,
        isWeeklyAdAudit: weekdayIndex === 1 && !milestone, // Every Monday
        dailyActual: currentActual.dailyLogs?.[d]
      });
    }

    return cells;
  }, [selectedMonth, currentActual]);

  const totals = useMemo(() => {
    const yearOneMonths = TWELVE_MONTH_ROADMAP.filter((m) => m.monthIndex >= 1);
    const projOrders = yearOneMonths.reduce((s, m) => s + m.expectedMonthlyOrders, 0);
    const projRev = yearOneMonths.reduce((s, m) => s + m.expectedGrossRevenue, 0);
    const projAd = yearOneMonths.reduce((s, m) => s + m.expectedAdSpend, 0);
    const projProfit = yearOneMonths.reduce((s, m) => s + m.expectedNetProfit, 0);

    const actOrders = Object.values(actualsByMonth).reduce((s, a) => s + (Number(a.actualOrders) || 0), 0);
    const actRev = Object.values(actualsByMonth).reduce((s, a) => s + (Number(a.actualRevenue) || 0), 0);
    const actAd = Object.values(actualsByMonth).reduce((s, a) => s + (Number(a.actualAdSpend) || 0), 0);
    const actProfit = Object.values(actualsByMonth).reduce((s, a) => s + (Number(a.actualNetProfit) || 0), 0);

    return {
      projOrders,
      projRev,
      projAd,
      projProfit,
      actOrders,
      actRev,
      actAd,
      actProfit
    };
  }, [actualsByMonth]);

  const liveBuyableCount = useMemo(
    () => products.filter((p) => String(p.amazonListing?.amazonLiveStatus || '').includes('BUYABLE')).length || 163,
    [products]
  );

  const updateActualField = (field: keyof MonthlyActualRecord, value: number | string | number[]) => {
    setActualsByMonth((prev) => {
      const existing = prev[selectedMonth.id] || {
        actualOrders: 0,
        actualRevenue: 0,
        actualAdSpend: 0,
        actualNetProfit: 0,
        completedTasks: [],
        notes: '',
        dailyLogs: {}
      };
      return {
        ...prev,
        [selectedMonth.id]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  const updateSelectedDayAdMetrics = (patch: Partial<DailyActualEntry>) => {
    setActualsByMonth((prev) => {
      const existing = prev[selectedMonth.id] || {
        actualOrders: 0,
        actualRevenue: 0,
        actualAdSpend: 0,
        actualNetProfit: 0,
        completedTasks: [],
        notes: '',
        dailyLogs: {}
      };
      const prevDayLog: DailyActualEntry = existing.dailyLogs?.[selectedDay] || {
        orders: 0,
        revenue: 0,
        clicks: 0,
        adSpend: 0,
        netProfit: 0
      };
      const nextDayLog: DailyActualEntry = {
        ...prevDayLog,
        ...patch
      };
      // If user updated clicks and didn't explicitly type a custom adSpend, auto-estimate cost at $0.23 avg clearing CPC
      if (patch.clicks !== undefined && patch.adSpend === undefined && prevDayLog.adSpend === +(Number(prevDayLog.clicks || 0) * 0.23).toFixed(2)) {
        nextDayLog.adSpend = +(Number(patch.clicks || 0) * 0.23).toFixed(2);
      }
      const nextDailyLogs = {
        ...(existing.dailyLogs || {}),
        [selectedDay]: nextDayLog
      };
      const totalDailyAdSpend = +Object.values(nextDailyLogs)
        .reduce((s, d) => s + (Number(d.adSpend) || 0), 0)
        .toFixed(2);

      return {
        ...prev,
        [selectedMonth.id]: {
          ...existing,
          actualAdSpend: totalDailyAdSpend > 0 ? totalDailyAdSpend : existing.actualAdSpend,
          dailyLogs: nextDailyLogs
        }
      };
    });
  };

  const toggleChecklistTask = (taskIndex: number) => {
    const existing = currentActual.completedTasks || [];
    const next = existing.includes(taskIndex)
      ? existing.filter((i) => i !== taskIndex)
      : [...existing, taskIndex];
    updateActualField('completedTasks', next);
  };

  const handleAutoEstimateProfit = () => {
    const orders = Number(currentActual.actualOrders || 0);
    const rev = Number(currentActual.actualRevenue || orders * 35.55);
    const ad = Number(currentActual.actualAdSpend || 0);
    const estNetBeforeAds = rev * 0.546;
    const estNet = +(estNetBeforeAds - ad).toFixed(2);
    setActualsByMonth((prev) => ({
      ...prev,
      [selectedMonth.id]: {
        ...currentActual,
        actualRevenue: +rev.toFixed(2),
        actualNetProfit: estNet
      }
    }));
    showToast(`Updated ${selectedMonth.monthLabel} actual revenue & net profit tracker.`);
  };

  if (!active) return null;

  const monthProgressPct = Math.min(
    100,
    Math.round(((currentActual.actualRevenue || 0) / Math.max(1, selectedMonth.expectedGrossRevenue)) * 100)
  );

  const selectedDayCell = calendarDays.find((c) => c.day === selectedDay);

  return (
    <section
      id="view-calendar"
      role="tabpanel"
      aria-labelledby="tab-calendar"
      className="workspace-panel active"
      style={{ paddingBottom: 72 }}
    >
      <div style={{ width: '100%', maxWidth: '100%', padding: '0 28px', boxSizing: 'border-box' }}>
        {/* JET-BLACK ARCHITECTURAL SPLIT HERO */}
        <div
          style={{
            background: '#09090B',
            color: '#FFFFFF',
            borderRadius: 16,
            padding: '36px 40px',
            marginBottom: 24,
            border: '1px solid #27272A',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 32,
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: '#FAF6EC',
                  color: '#9E782F',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '4px 10px',
                  borderRadius: 999
                }}
              >
                07 · DEDICATED BUSINESS CALENDAR & GOAL TRACKER
              </span>
              <span style={{ fontSize: 12, color: '#4ADE80', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                ● {liveBuyableCount}/{products.length || 206} SKUs Active · Year-1 Goal: ${totals.projRev.toLocaleString()}
              </span>
            </div>

            <h1
              style={{
                fontSize: 'clamp(26px, 3vw, 36px)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                margin: '0 0 14px 0',
                color: '#FFFFFF'
              }}
            >
              12-Month Business Calendar,{' '}
              <span style={{ color: '#D9B668' }}>Daily Expectations & Goals</span>
            </h1>

            <p style={{ color: '#D4D4D8', fontSize: 15, lineHeight: 1.6, margin: '0 0 22px 0', maxWidth: 620 }}>
              Click any month below to view its <strong style={{ color: '#FFFFFF' }}>day-by-day visual calendar</strong>,{' '}
              <strong style={{ color: '#FFFFFF' }}>expected vs. actual sales</strong>,{' '}
              <strong style={{ color: '#FFFFFF' }}>Amazon bi-weekly payout dates</strong>,{' '}
              <strong style={{ color: '#FFFFFF' }}>ad optimization milestones</strong>, and{' '}
              <strong style={{ color: '#FFFFFF' }}>projected profit goals</strong>.
            </p>

            {/* 12-MONTH PILL SELECTOR */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TWELVE_MONTH_ROADMAP.map((m) => {
                const isSelected = m.id === selectedMonth.id;
                const hasActual = (actualsByMonth[m.id]?.actualOrders || 0) > 0;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => selectMonthWithReset(m.id, m.id === '2026-10' ? 22 : 1)}
                    style={{
                      background: isSelected ? '#FFFFFF' : '#18181B',
                      color: isSelected ? '#09090B' : '#E4E4E7',
                      border: isSelected ? '1px solid #FFFFFF' : hasActual ? '1px solid #D9B668' : '1px solid #27272A',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontVariantNumeric: 'tabular-nums',
                      minHeight: 38,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <span style={{ color: isSelected ? '#9E782F' : '#D9B668' }}>
                      {String(m.monthIndex).padStart(2, '0')}
                    </span>
                    <span>{m.shortLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT SUMMARY CARD: 12-MONTH ANNUAL GOALS & YTD ACTUALS */}
          <div
            style={{
              background: '#18181B',
              border: '1px solid #27272A',
              borderRadius: 12,
              padding: 24
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #27272A',
                paddingBottom: 12,
                marginBottom: 16
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', color: '#D9B668', textTransform: 'uppercase' }}>
                YEAR-1 PROJECTED GOALS (12 MONTHS)
              </span>
              <span style={{ fontSize: 12, color: '#A1A1AA', fontVariantNumeric: 'tabular-nums' }}>
                Oct 2026 – Sep 2027
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
                marginBottom: 16
              }}
            >
              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  12-Mo Expected Revenue
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  ${totals.projRev.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#D9B668', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                  {totals.projOrders.toLocaleString()} projected orders
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  12-Mo Expected Net Profit
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#4ADE80', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  +${totals.projProfit.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                  After ${totals.projAd.toLocaleString()} total ad spend
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Logged YTD Actual Sales
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#FFFFFF', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  ${totals.actRev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 11, color: '#D4D4D8', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                  {totals.actOrders} actual orders logged
                </div>
              </div>

              <div style={{ background: '#09090B', padding: 14, borderRadius: 8, border: '1px solid #27272A' }}>
                <div style={{ fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Logged YTD Net Profit
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#4ADE80', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  +${totals.actProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 11, color: '#A1A1AA', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                  ${totals.actAd.toFixed(2)} ad spend logged
                </div>
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#D4D4D8', lineHeight: 1.5 }}>
              <strong style={{ color: '#D9B668' }}>Scaling Rule:</strong> Every SKU runs on $0-inventory US Dropship first. Any SKU reaching{' '}
              <strong style={{ color: '#FFFFFF' }}>≥15 orders/mo</strong> graduates to a 150-unit Alibaba FBA Prime batch (+14% net margin boost).
            </div>
          </div>
        </div>

        {/* SIDE-BY-SIDE FULL-WIDTH WORKSPACE: LEFT = VISUAL CALENDAR GRID, RIGHT = CALENDAR DETAILS & LOGGER */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(640px, 1.55fr) minmax(440px, 1fr)',
            gap: 24,
            marginBottom: 28,
            alignItems: 'start'
          }}
        >
          {/* LEFT SIDE: FULL VISUAL MONTHLY CALENDAR GRID (SUN - SAT) */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 14,
              padding: 24,
              position: 'sticky',
              top: 84
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: 18,
                paddingBottom: 14,
                borderBottom: '1px solid #E4E4E7'
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#9E782F',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em'
                  }}
                >
                  {selectedMonth.seasonTag} · VISUAL DAY-BY-DAY OPERATING CALENDAR
                </span>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0', letterSpacing: '-0.025em' }}>
                  {selectedMonth.monthLabel} — {selectedMonth.phaseTitle}
                </h2>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                {/* PREV / CURRENT / NEXT MONTH NAVIGATION CONTROLS */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={!prevMonth}
                    onClick={() => prevMonth && selectMonthWithReset(prevMonth.id, 1)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: prevMonth ? '#FFFFFF' : '#F3F4F6',
                      color: prevMonth ? '#09090B' : '#A1A1AA',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: prevMonth ? 'pointer' : 'not-allowed',
                      minHeight: 38,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    ← Prev ({prevMonth ? prevMonth.shortLabel : 'Start'})
                  </button>

                  <select
                    aria-label="Select operating month"
                    value={selectedMonth.id}
                    onChange={(e) => selectMonthWithReset(e.target.value, e.target.value === '2026-10' ? 22 : 1)}
                    style={{
                      height: 38,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: '2px solid #09090B',
                      background: '#09090B',
                      color: '#FFFFFF',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {TWELVE_MONTH_ROADMAP.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.shortLabel} — {m.phaseTitle}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    disabled={!nextMonth}
                    onClick={() => nextMonth && selectMonthWithReset(nextMonth.id, 1)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: nextMonth ? '#FAF6EC' : '#F3F4F6',
                      color: nextMonth ? '#9E782F' : '#A1A1AA',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: nextMonth ? 'pointer' : 'not-allowed',
                      minHeight: 38,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    Next ({nextMonth ? nextMonth.shortLabel : 'End'}) →
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#FAF6EC', color: '#9E782F' }}>
                    💰 Bi-Weekly Payout
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#09090B', color: '#FFFFFF' }}>
                    🔥 Retail Peak / Event
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#F3F4F6', color: '#3F3F46' }}>
                    ⚡ Monday Ad Audit
                  </span>
                </div>
              </div>
            </div>

            {/* QUICK 3-MONTH EXPECTATIONS STRIP RIGHT ABOVE THE CALENDAR GRID (PREV vs. CURRENT vs. NEXT) */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 16,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {/* PREVIOUS MONTH MINI EXPECTATION */}
              <div
                onClick={() => comparedPrevMonth && selectMonthWithReset(comparedPrevMonth.id, 1)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: '#F8F8F9',
                  border: '1px solid #E4E4E7',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    ← PREVIOUS: {comparedPrevMonth.shortLabel}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#09090B' }}>
                    ${comparedPrevMonth.expectedGrossRevenue.toLocaleString()} ({comparedPrevMonth.expectedMonthlyOrders} ord)
                  </span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#09090B', marginBottom: 2 }}>
                  {comparedPrevMonth.phaseTitle}
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {comparedPrevMonth.whatToExpect}
                </div>
              </div>

              {/* CURRENT SELECTED MONTH MINI EXPECTATION */}
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: '#FAF6EC',
                  border: '2px solid #9E782F'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    ● VIEWING: {selectedMonth.shortLabel}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#09090B' }}>
                    ${selectedMonth.expectedGrossRevenue.toLocaleString()} ({selectedMonth.expectedMonthlyOrders} ord)
                  </span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#09090B', marginBottom: 2 }}>
                  {selectedMonth.phaseTitle}
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {selectedMonth.whatToExpect}
                </div>
              </div>

              {/* NEXT MONTH MINI EXPECTATION */}
              <div
                onClick={() => comparedNextMonth && selectMonthWithReset(comparedNextMonth.id, 1)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: '#F8F8F9',
                  border: '1px solid #E4E4E7',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    NEXT: {comparedNextMonth.shortLabel} →
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#15803D' }}>
                    ${comparedNextMonth.expectedGrossRevenue.toLocaleString()} ({comparedNextMonth.expectedMonthlyOrders} ord)
                  </span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#09090B', marginBottom: 2 }}>
                  {comparedNextMonth.phaseTitle}
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {comparedNextMonth.whatToExpect}
                </div>
              </div>
            </div>

            {/* 7-COLUMN WEEKDAY HEADER */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(95px, 1fr))',
                gap: 8,
                marginBottom: 8
              }}
            >
              {WEEKDAYS.map((dayName) => (
                <div
                  key={dayName}
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#71717A',
                    letterSpacing: '0.08em',
                    padding: '6px 0'
                  }}
                >
                  {dayName}
                </div>
              ))}
            </div>

            {/* 7-COLUMN DAY CELLS */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(95px, 1fr))',
                gap: 8,
                overflowX: 'auto'
              }}
            >
              {calendarDays.map((cell, idx) => {
                if (cell.day === null) {
                  return (
                    <div
                      key={`empty-${idx}`}
                      style={{
                        minHeight: 118,
                        background: '#F8F8F9',
                        borderRadius: 10,
                        border: '1px dashed #E4E4E7'
                      }}
                    />
                  );
                }

                const isToday = selectedMonth.id === '2026-10' && cell.day === 24;
                const isSelectedDay = cell.day === selectedDay;
                const hasMilestone = Boolean(cell.milestone);

                return (
                  <button
                    key={cell.day}
                    type="button"
                    onClick={() => setSelectedDay(cell.day!)}
                    style={{
                      minHeight: 118,
                      textAlign: 'left',
                      padding: 10,
                      borderRadius: 10,
                      background: isSelectedDay ? '#FAF6EC' : '#FFFFFF',
                      border: isSelectedDay
                        ? '2px solid #9E782F'
                        : isToday
                        ? '2px solid #09090B'
                        : '1px solid #E4E4E7',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: isToday ? '#FFFFFF' : '#09090B',
                          background: isToday ? '#09090B' : 'transparent',
                          padding: isToday ? '2px 7px' : 0,
                          borderRadius: 6
                        }}
                      >
                        {cell.day}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#71717A' }}>
                        Est: ${cell.expectedRev}
                      </span>
                    </div>

                    {hasMilestone && cell.milestone && (
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 6px',
                          borderRadius: 5,
                          background:
                            cell.milestone.type === 'holiday' || cell.milestone.type === 'launch'
                              ? '#09090B'
                              : '#FAF6EC',
                          color:
                            cell.milestone.type === 'holiday' || cell.milestone.type === 'launch'
                              ? '#FFFFFF'
                              : '#9E782F',
                          border:
                            cell.milestone.type === 'holiday' || cell.milestone.type === 'launch'
                              ? 'none'
                              : '1px solid #D9B668',
                          lineHeight: 1.25,
                          margin: '4px 0'
                        }}
                      >
                        {cell.milestone.label}
                      </div>
                    )}

                    {!hasMilestone && cell.isWeeklyAdAudit && (
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 5,
                          background: '#F3F4F6',
                          color: '#3F3F46',
                          margin: '4px 0'
                        }}
                      >
                        ⚡ Ad Bid Check
                      </div>
                    )}

                    <div style={{ width: '100%', borderTop: '1px solid #F3F4F6', paddingTop: 4, marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {cell.dailyActual &&
                      ((cell.dailyActual.impressions || 0) > 0 ||
                        (cell.dailyActual.clicks || 0) > 0 ||
                        (cell.dailyActual.adSpend || 0) > 0) ? (
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: '#09090B',
                            background: '#FAF6EC',
                            border: '1px solid #D9B668',
                            borderRadius: 4,
                            padding: '2px 5px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={`Live/Logged Ads on Day ${cell.day}: ${cell.dailyActual.impressions || 0} impressions · ${cell.dailyActual.clicks || 0} clicks · $${Number(cell.dailyActual.adSpend || 0).toFixed(2)} spend`}
                        >
                          📣 {(cell.dailyActual.impressions || 0).toLocaleString()} imp · 🎯 {cell.dailyActual.clicks || 0} clk
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#9E782F',
                            background: '#FAF6EC',
                            borderRadius: 4,
                            padding: '2px 5px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={`Projected Ads on Day ${cell.day}: ~${cell.expectedImpressions.toLocaleString()} impressions · ~${cell.expectedClicks} clicks (~$${cell.expectedAdSpend.toFixed(2)} est. spend)`}
                        >
                          📣 ~{cell.expectedImpressions >= 1000 ? `${(cell.expectedImpressions / 1000).toFixed(1)}k` : cell.expectedImpressions} imp · 🎯 ~{cell.expectedClicks} clk
                        </div>
                      )}
                      {cell.dailyActual && cell.dailyActual.orders > 0 ? (
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#15803D' }}>
                          ✓ {cell.dailyActual.orders} ord · ${cell.dailyActual.revenue.toFixed(0)}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: '#3F3F46', fontWeight: 600 }}>
                          Goal: ~{cell.expectedOrders} ord
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT SIDE OF THE CALENDAR: INDEPENDENT SCROLL VIEW FOR SELECTED DAY DETAILS, EXPECTATIONS & LOGGER */}
          <div
            role="region"
            aria-label="Calendar details and monthly expectations scroll panel"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              maxHeight: 'calc(100vh - 104px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              paddingRight: 8,
              paddingBottom: 16,
              position: 'sticky',
              top: 84,
              scrollbarWidth: 'thin'
            }}
          >
            {/* SELECTED DAY DETAILS CARD (TOP OF RIGHT PANEL) */}
            {selectedDayCell && (
              <div
                style={{
                  padding: '20px 24px',
                  borderRadius: 14,
                  background: '#09090B',
                  color: '#FFFFFF',
                  border: '1px solid #27272A'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#D9B668', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      SELECTED CALENDAR DAY DETAILS · {selectedMonth.monthLabel.toUpperCase()} {selectedDayCell.day}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF', marginTop: 4 }}>
                      {selectedDayCell.milestone
                        ? `${selectedDayCell.milestone.label}`
                        : `Standard Operating Day — Monitor Buy Box, Ad Impressions/Clicks & Supplier Auto-Routing`}
                    </div>
                  </div>
                  <span
                    style={{
                      background: '#18181B',
                      border: '1px solid #27272A',
                      color: '#D9B668',
                      padding: '5px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    Day {selectedDayCell.day} of {calendarDays.filter((c) => c.day !== null).length}
                  </span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 10,
                    paddingTop: 14,
                    borderTop: '1px solid #27272A',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  <div style={{ background: '#18181B', padding: '10px 12px', borderRadius: 8, border: '1px solid #27272A' }}>
                    <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>Daily Sales Target</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginTop: 3 }}>
                      {selectedDayCell.expectedOrders} ord · {formatMoney(selectedDayCell.expectedRev)}
                    </div>
                    <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                      Ads Goal: ~{selectedDayCell.expectedImpressions.toLocaleString()} imp · ~{selectedDayCell.expectedClicks} clk
                    </div>
                  </div>
                  <div style={{ background: '#18181B', padding: '10px 12px', borderRadius: 8, border: '1px solid #27272A' }}>
                    <div style={{ fontSize: 10, color: '#A1A1AA', textTransform: 'uppercase', fontWeight: 700 }}>Logged Actual Sales</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#34D399', marginTop: 3 }}>
                      {selectedDayCell.dailyActual?.orders || 0} ord · {formatMoney(selectedDayCell.dailyActual?.revenue || 0)}
                    </div>
                    <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
                      Est. Profit: {formatMoney(selectedDayCell.dailyActual?.netProfit || 0)}
                    </div>
                  </div>
                  <div style={{ background: '#18181B', padding: '10px 12px', borderRadius: 8, border: '1px solid #27272A' }}>
                    <div style={{ fontSize: 10, color: '#D9B668', textTransform: 'uppercase', fontWeight: 700 }}>Ads Impressions &amp; Clicks</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#FDE68A', marginTop: 3 }}>
                      {(selectedDayCell.dailyActual?.impressions ?? 0).toLocaleString()} imp · {selectedDayCell.dailyActual?.clicks || 0} clk
                    </div>
                    <div style={{ fontSize: 11, color: '#D9B668', marginTop: 2 }}>
                      Spend: ${Number(selectedDayCell.dailyActual?.adSpend || 0).toFixed(2)} (Est: ${selectedDayCell.expectedAdSpend.toFixed(2)})
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* TOP RIGHT CARD: WHAT TO EXPECT THIS MONTH + CHECKLIST + KPIS */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 14,
              padding: 24
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              <span
                style={{
                  background: '#FAF6EC',
                  color: '#9E782F',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  padding: '4px 10px',
                  borderRadius: 999
                }}
              >
                WHAT TO EXPECT IN {selectedMonth.monthLabel.toUpperCase()}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#71717A', fontVariantNumeric: 'tabular-nums' }}>
                Selected Day: {selectedMonth.shortLabel.split(' ')[0]} {selectedDay} (Goal: ~{selectedDayCell?.expectedOrders || selectedMonth.expectedDailyOrders} orders)
              </span>
            </div>

            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#09090B', letterSpacing: '-0.025em', margin: '0 0 10px 0' }}>
              {selectedMonth.phaseTitle}
            </h3>

            <p style={{ fontSize: 14, color: '#3F3F46', lineHeight: 1.65, margin: '0 0 20px 0' }}>
              {selectedMonth.whatToExpect}
            </p>

            {/* ARCHITECTURAL 4-BOX SPEC STRIP FOR THIS MONTH'S PROJECTED GOALS */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
                background: '#F8F8F9',
                border: '1px solid #E4E4E7',
                borderRadius: 10,
                marginBottom: 22,
                overflow: 'hidden'
              }}
            >
              <div style={{ padding: 14, borderRight: '1px solid #E4E4E7' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Expected Sales
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  ${selectedMonth.expectedGrossRevenue.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.expectedMonthlyOrders} orders
                </div>
              </div>

              <div style={{ padding: 14, borderRight: '1px solid #E4E4E7' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Expected Net Profit
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  +${selectedMonth.expectedNetProfit.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#9E782F', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.expectedNetMarginPct}% net margin
                </div>
              </div>

              <div style={{ padding: 14, borderRight: '1px solid #E4E4E7' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Expected Ad Cost
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  ${selectedMonth.expectedAdSpend.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.targetAcosPct}% ACOS · {selectedMonth.targetTacosPct}% TACoS
                </div>
              </div>

              <div style={{ padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Catalog & FBA Goal
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                  {selectedMonth.activeSkusGoal} SKUs
                </div>
                <div style={{ fontSize: 11, color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.fbaHeroSkusGoal} FBA Prime Heroes
                </div>
              </div>
            </div>

            {/* MONTHLY ACTION CHECKLIST */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#09090B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                {selectedMonth.monthLabel} Execution Checklist (Click to Check Off)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedMonth.keyFocusChecklist.map((task, idx) => {
                  const checked = (currentActual.completedTasks || []).includes(idx);
                  return (
                    <label
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: checked ? '#FAF6EC' : '#F8F8F9',
                        border: checked ? '1px solid #D9B668' : '1px solid #E4E4E7',
                        cursor: 'pointer',
                        fontSize: 13,
                        color: '#09090B',
                        fontWeight: checked ? 600 : 500
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleChecklistTask(idx)}
                        style={{ marginTop: 2, width: 16, height: 16, accentColor: '#09090B' }}
                      />
                      <span style={{ textDecoration: checked ? 'line-through' : 'none', color: checked ? '#71717A' : '#09090B' }}>
                        {task}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* GUARDRAIL HEALTH KPIS TO MONITOR */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 8,
                borderTop: '1px solid #E4E4E7',
                paddingTop: 14
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: '#71717A', textTransform: 'uppercase', fontWeight: 700 }}>Buy Box Goal</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.kpisToWatch.buyBoxTarget}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#71717A', textTransform: 'uppercase', fontWeight: 700 }}>Order Defect Rate</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.kpisToWatch.odrTarget}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#71717A', textTransform: 'uppercase', fontWeight: 700 }}>Valid Tracking</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.kpisToWatch.validTrackingTarget}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#71717A', textTransform: 'uppercase', fontWeight: 700 }}>Max Return Rate</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', fontVariantNumeric: 'tabular-nums' }}>
                  {selectedMonth.kpisToWatch.returnRateTarget}
                </div>
              </div>
            </div>
          </div>

          {/* PREVIOUS · CURRENT · NEXT MONTH EXPECTATIONS & SIDE-BY-SIDE COMPARISON CARD */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 14,
              padding: 24
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div>
                <span
                  style={{
                    background: '#FAF6EC',
                    color: '#9E782F',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    padding: '4px 10px',
                    borderRadius: 999,
                    textTransform: 'uppercase'
                  }}
                >
                  3-MONTH EXPECTATIONS & SIDE-BY-SIDE COMPARISON
                </span>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: '#09090B', margin: '8px 0 0 0', letterSpacing: '-0.025em' }}>
                  Compare {comparedPrevMonth.shortLabel} (Prev) vs. {selectedMonth.shortLabel} (Current) vs. {comparedNextMonth.shortLabel} (Next)
                </h3>
              </div>

              {/* CUSTOM COMPARE MONTH SELECTORS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#71717A', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Prev:
                  <select
                    aria-label="Select previous comparison month"
                    value={comparedPrevMonth.id}
                    onChange={(e) => setCustomComparePrevId(e.target.value)}
                    style={{
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid #D4D4D8',
                      background: '#F8F8F9',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#09090B'
                    }}
                  >
                    {TWELVE_MONTH_ROADMAP.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.shortLabel}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ fontSize: 11, fontWeight: 700, color: '#71717A', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Next:
                  <select
                    aria-label="Select next comparison month"
                    value={comparedNextMonth.id}
                    onChange={(e) => setCustomCompareNextId(e.target.value)}
                    style={{
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid #D4D4D8',
                      background: '#F8F8F9',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#09090B'
                    }}
                  >
                    {TWELVE_MONTH_ROADMAP.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.shortLabel}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {/* FULL EXPECTATIONS NARRATIVES FOR PREVIOUS, CURRENT & NEXT MONTH */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                marginBottom: 18
              }}
            >
              {/* PREVIOUS MONTH EXPECTATIONS */}
              <div
                style={{
                  background: '#F8F8F9',
                  border: '1px solid #E4E4E7',
                  borderRadius: 10,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      ← PREVIOUS · {comparedPrevMonth.monthLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => selectMonthWithReset(comparedPrevMonth.id, 1)}
                      style={{
                        border: '1px solid #D4D4D8',
                        background: '#FFFFFF',
                        color: '#09090B',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '3px 7px',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Open
                    </button>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', marginBottom: 6 }}>
                    {comparedPrevMonth.phaseTitle}
                  </div>
                  <p style={{ fontSize: 12, color: '#3F3F46', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                    {comparedPrevMonth.whatToExpect}
                  </p>
                </div>
                <div style={{ fontSize: 11, color: '#09090B', fontWeight: 700, borderTop: '1px solid #E4E4E7', paddingTop: 8 }}>
                  Top Focus: <span style={{ fontWeight: 500, color: '#3F3F46' }}>{comparedPrevMonth.keyFocusChecklist[0]}</span>
                </div>
              </div>

              {/* CURRENT MONTH EXPECTATIONS */}
              <div
                style={{
                  background: '#FAF6EC',
                  border: '2px solid #9E782F',
                  borderRadius: 10,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      ● CURRENT · {selectedMonth.monthLabel}
                    </span>
                    <span
                      style={{
                        background: '#09090B',
                        color: '#FFFFFF',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '3px 7px',
                        borderRadius: 6
                      }}
                    >
                      Viewing
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', marginBottom: 6 }}>
                    {selectedMonth.phaseTitle}
                  </div>
                  <p style={{ fontSize: 12, color: '#09090B', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                    {selectedMonth.whatToExpect}
                  </p>
                </div>
                <div style={{ fontSize: 11, color: '#09090B', fontWeight: 700, borderTop: '1px solid #D9B668', paddingTop: 8 }}>
                  Top Focus: <span style={{ fontWeight: 600, color: '#3F3F46' }}>{selectedMonth.keyFocusChecklist[0]}</span>
                </div>
              </div>

              {/* NEXT MONTH EXPECTATIONS */}
              <div
                style={{
                  background: '#F8F8F9',
                  border: '1px solid #E4E4E7',
                  borderRadius: 10,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      NEXT · {comparedNextMonth.monthLabel} →
                    </span>
                    <button
                      type="button"
                      onClick={() => selectMonthWithReset(comparedNextMonth.id, 1)}
                      style={{
                        border: '1px solid #D9B668',
                        background: '#FAF6EC',
                        color: '#9E782F',
                        fontSize: 10,
                        fontWeight: 800,
                        padding: '3px 7px',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Open →
                    </button>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#09090B', marginBottom: 6 }}>
                    {comparedNextMonth.phaseTitle}
                  </div>
                  <p style={{ fontSize: 12, color: '#3F3F46', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                    {comparedNextMonth.whatToExpect}
                  </p>
                </div>
                <div style={{ fontSize: 11, color: '#09090B', fontWeight: 700, borderTop: '1px solid #E4E4E7', paddingTop: 8 }}>
                  Top Focus: <span style={{ fontWeight: 500, color: '#3F3F46' }}>{comparedNextMonth.keyFocusChecklist[0]}</span>
                </div>
              </div>
            </div>

            {/* SIDE-BY-SIDE METRIC COMPARISON TABLE (PREV vs. CURRENT vs. NEXT) */}
            <div style={{ overflowX: 'auto', border: '1px solid #E4E4E7', borderRadius: 10 }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                <thead>
                  <tr style={{ background: '#09090B', color: '#FFFFFF', textAlign: 'left' }}>
                    <th scope="col" style={{ padding: '10px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      KPI / Metric
                    </th>
                    <th scope="col" style={{ padding: '10px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'right' }}>
                      ← {comparedPrevMonth.shortLabel} (Prev)
                    </th>
                    <th scope="col" style={{ padding: '10px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'right', color: '#D9B668' }}>
                      ● {selectedMonth.shortLabel} (Current)
                    </th>
                    <th scope="col" style={{ padding: '10px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'right' }}>
                      {comparedNextMonth.shortLabel} (Next) →
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* ROW 1: EXPECTED GROSS SALES */}
                  <tr style={{ borderBottom: '1px solid #E4E4E7' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Expected Gross Sales</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      ${comparedPrevMonth.expectedGrossRevenue.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#09090B' }}>
                      ${selectedMonth.expectedGrossRevenue.toLocaleString()}
                      <div style={{ fontSize: 10, color: selectedMonth.expectedGrossRevenue >= comparedPrevMonth.expectedGrossRevenue ? '#15803D' : '#B91C1C', fontWeight: 700 }}>
                        {selectedMonth.expectedGrossRevenue >= comparedPrevMonth.expectedGrossRevenue ? '+' : ''}
                        {Math.round(((selectedMonth.expectedGrossRevenue - comparedPrevMonth.expectedGrossRevenue) / Math.max(1, comparedPrevMonth.expectedGrossRevenue)) * 100)}% vs {comparedPrevMonth.shortLabel}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                      ${comparedNextMonth.expectedGrossRevenue.toLocaleString()}
                      <div style={{ fontSize: 10, color: comparedNextMonth.expectedGrossRevenue >= selectedMonth.expectedGrossRevenue ? '#15803D' : '#9E782F', fontWeight: 700 }}>
                        {comparedNextMonth.expectedGrossRevenue >= selectedMonth.expectedGrossRevenue ? '+' : ''}
                        {Math.round(((comparedNextMonth.expectedGrossRevenue - selectedMonth.expectedGrossRevenue) / Math.max(1, selectedMonth.expectedGrossRevenue)) * 100)}% vs {selectedMonth.shortLabel}
                      </div>
                    </td>
                  </tr>

                  {/* ROW 2: LOGGED ACTUAL SALES */}
                  <tr style={{ borderBottom: '1px solid #E4E4E7', background: '#F8F8F9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Logged Actual Sales</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      ${Number(comparedPrevActual.actualRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({comparedPrevActual.actualOrders || 0} ord)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#09090B' }}>
                      ${Number(currentActual.actualRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({currentActual.actualOrders || 0} ord)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      ${Number(comparedNextActual.actualRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({comparedNextActual.actualOrders || 0} ord)
                    </td>
                  </tr>

                  {/* ROW 3: EXPECTED ORDERS & DAILY PACE */}
                  <tr style={{ borderBottom: '1px solid #E4E4E7' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Expected Orders (Daily Pace)</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      {comparedPrevMonth.expectedMonthlyOrders} ord ({comparedPrevMonth.expectedDailyOrders}/day)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#09090B' }}>
                      {selectedMonth.expectedMonthlyOrders} ord ({selectedMonth.expectedDailyOrders}/day)
                      <div style={{ fontSize: 10, color: '#9E782F', fontWeight: 700 }}>
                        {selectedMonth.expectedMonthlyOrders >= comparedPrevMonth.expectedMonthlyOrders ? '+' : ''}
                        {selectedMonth.expectedMonthlyOrders - comparedPrevMonth.expectedMonthlyOrders} ord vs Prev
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                      {comparedNextMonth.expectedMonthlyOrders} ord ({comparedNextMonth.expectedDailyOrders}/day)
                      <div style={{ fontSize: 10, color: '#15803D', fontWeight: 700 }}>
                        {comparedNextMonth.expectedMonthlyOrders >= selectedMonth.expectedMonthlyOrders ? '+' : ''}
                        {comparedNextMonth.expectedMonthlyOrders - selectedMonth.expectedMonthlyOrders} ord vs Current
                      </div>
                    </td>
                  </tr>

                  {/* ROW 4: EXPECTED NET PROFIT & MARGIN */}
                  <tr style={{ borderBottom: '1px solid #E4E4E7', background: '#F8F8F9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Expected Net Profit (Margin)</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      +${comparedPrevMonth.expectedNetProfit.toLocaleString()} ({comparedPrevMonth.expectedNetMarginPct}%)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#15803D' }}>
                      +${selectedMonth.expectedNetProfit.toLocaleString()} ({selectedMonth.expectedNetMarginPct}%)
                      <div style={{ fontSize: 10, color: '#09090B', fontWeight: 700 }}>
                        {selectedMonth.expectedNetProfit >= comparedPrevMonth.expectedNetProfit ? '+$' : '-$'}
                        {Math.abs(selectedMonth.expectedNetProfit - comparedPrevMonth.expectedNetProfit).toLocaleString()} vs Prev
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#15803D' }}>
                      +${comparedNextMonth.expectedNetProfit.toLocaleString()} ({comparedNextMonth.expectedNetMarginPct}%)
                      <div style={{ fontSize: 10, color: '#09090B', fontWeight: 700 }}>
                        {comparedNextMonth.expectedNetProfit >= selectedMonth.expectedNetProfit ? '+$' : '-$'}
                        {Math.abs(comparedNextMonth.expectedNetProfit - selectedMonth.expectedNetProfit).toLocaleString()} vs Current
                      </div>
                    </td>
                  </tr>

                  {/* ROW 5: EXPECTED AD SPEND & ACOS */}
                  <tr style={{ borderBottom: '1px solid #E4E4E7' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Expected Ad Budget (Target ACOS)</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      ${comparedPrevMonth.expectedAdSpend.toLocaleString()} ({comparedPrevMonth.targetAcosPct}% ACOS)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#09090B' }}>
                      ${selectedMonth.expectedAdSpend.toLocaleString()} ({selectedMonth.targetAcosPct}% ACOS)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                      ${comparedNextMonth.expectedAdSpend.toLocaleString()} ({comparedNextMonth.targetAcosPct}% ACOS)
                    </td>
                  </tr>

                  {/* ROW 6: ACTIVE CATALOG & FBA PRIME HEROES */}
                  <tr>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#09090B' }}>Catalog SKUs · FBA Prime Heroes</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3F3F46', fontWeight: 600 }}>
                      {comparedPrevMonth.activeSkusGoal} SKUs · {comparedPrevMonth.fbaHeroSkusGoal} FBA
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', background: '#FAF6EC', fontWeight: 800, color: '#09090B' }}>
                      {selectedMonth.activeSkusGoal} SKUs · {selectedMonth.fbaHeroSkusGoal} FBA
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#09090B' }}>
                      {comparedNextMonth.activeSkusGoal} SKUs · {comparedNextMonth.fbaHeroSkusGoal} FBA
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT: STICKY MONTHLY ACTUALS vs EXPECTED LOGGER CARD */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #D4D4D8',
              borderRadius: 14,
              padding: 28
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  MONTHLY SCOREBOARD & ACTUALS LOGGER
                </span>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: '#09090B', margin: '4px 0 0 0' }}>
                  {selectedMonth.monthLabel} · Actual vs. Expected
                </h3>
              </div>
              <span
                style={{
                  background: '#09090B',
                  color: '#FFFFFF',
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {monthProgressPct}% of Revenue Goal
              </span>
            </div>

            {/* PROGRESS BAR */}
            <div
              style={{
                height: 8,
                background: '#F3F4F6',
                borderRadius: 999,
                overflow: 'hidden',
                marginBottom: 16,
                border: '1px solid #E4E4E7'
              }}
            >
              <div
                style={{
                  width: `${monthProgressPct}%`,
                  height: '100%',
                  background: '#9E782F',
                  transition: 'width 250ms ease'
                }}
              />
            </div>

            {/* SELECTED DAY AD IMPRESSIONS, CLICKS & COST TRACKER */}
            <div
              style={{
                background: '#FAF6EC',
                border: '1px solid #D9B668',
                borderRadius: 10,
                padding: 14,
                marginBottom: 16
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#9E782F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  📣 {selectedMonth.shortLabel.split(' ')[0]} {selectedDay} · DAILY AD IMPRESSIONS, CLICKS &amp; COST
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                  Showing on Day {selectedDay}: {(currentActual.dailyLogs?.[selectedDay]?.impressions || 0).toLocaleString()} imp ·{' '}
                  {currentActual.dailyLogs?.[selectedDay]?.clicks || 0} clicks · $
                  {Number(currentActual.dailyLogs?.[selectedDay]?.adSpend || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <div>
                  <label
                    htmlFor="cal-day-impressions"
                    style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                  >
                    Ad Impressions (Day {selectedDay})
                  </label>
                  <input
                    id="cal-day-impressions"
                    type="number"
                    min="0"
                    step="1"
                    value={currentActual.dailyLogs?.[selectedDay]?.impressions || 0}
                    onChange={(e) => updateSelectedDayAdMetrics({ impressions: Math.max(0, Number(e.target.value) || 0) })}
                    style={{
                      width: '100%',
                      height: 42,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: '#FFFFFF',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#09090B',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="cal-day-clicks"
                    style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                  >
                    Ad Clicks (Day {selectedDay})
                  </label>
                  <input
                    id="cal-day-clicks"
                    type="number"
                    min="0"
                    step="1"
                    value={currentActual.dailyLogs?.[selectedDay]?.clicks || 0}
                    onChange={(e) => updateSelectedDayAdMetrics({ clicks: Math.max(0, Number(e.target.value) || 0) })}
                    style={{
                      width: '100%',
                      height: 42,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: '#FFFFFF',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#09090B',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="cal-day-adcost"
                    style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 4 }}
                  >
                    Ad Cost ($) (Day {selectedDay})
                  </label>
                  <input
                    id="cal-day-adcost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentActual.dailyLogs?.[selectedDay]?.adSpend || 0}
                    onChange={(e) => updateSelectedDayAdMetrics({ adSpend: Math.max(0, Number(e.target.value) || 0) })}
                    style={{
                      width: '100%',
                      height: 42,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: '1px solid #D4D4D8',
                      background: '#FFFFFF',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#09090B',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 16 }}>
              <div>
                <label
                  htmlFor="cal-actual-orders"
                  style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                >
                  Actual Orders (Goal: {selectedMonth.expectedMonthlyOrders})
                </label>
                <input
                  id="cal-actual-orders"
                  type="number"
                  min="0"
                  step="1"
                  value={currentActual.actualOrders}
                  onChange={(e) => updateActualField('actualOrders', Number(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 46,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #D4D4D8',
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#09090B',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="cal-actual-revenue"
                  style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                >
                  Actual Sales $ (Goal: ${selectedMonth.expectedGrossRevenue.toLocaleString()})
                </label>
                <input
                  id="cal-actual-revenue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={currentActual.actualRevenue}
                  onChange={(e) => updateActualField('actualRevenue', Number(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 46,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #D4D4D8',
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#09090B',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="cal-actual-adspend"
                  style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                >
                  Actual Ad Spend $ (Est: ${selectedMonth.expectedAdSpend})
                </label>
                <input
                  id="cal-actual-adspend"
                  type="number"
                  min="0"
                  step="0.01"
                  value={currentActual.actualAdSpend}
                  onChange={(e) => updateActualField('actualAdSpend', Number(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 46,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #D4D4D8',
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#09090B',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="cal-actual-profit"
                  style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
                >
                  Actual Net Profit $ (Goal: +${selectedMonth.expectedNetProfit.toLocaleString()})
                </label>
                <input
                  id="cal-actual-profit"
                  type="number"
                  step="0.01"
                  value={currentActual.actualNetProfit}
                  onChange={(e) => updateActualField('actualNetProfit', Number(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 46,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #D4D4D8',
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#09090B',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="cal-actual-notes"
                style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#3F3F46', textTransform: 'uppercase', marginBottom: 6 }}
              >
                Monthly Executive Notes / Winning SKUs
              </label>
              <textarea
                id="cal-actual-notes"
                rows={2}
                value={currentActual.notes}
                onChange={(e) => updateActualField('notes', e.target.value)}
                placeholder="Log winning SKUs, ad adjustments, or FBA restock notes for this month…"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #D4D4D8',
                  fontSize: 13,
                  color: '#09090B',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleAutoEstimateProfit}
                style={{
                  background: '#09090B',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 18px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 44
                }}
              >
                Auto-Calculate Revenue & Net Profit from Orders
              </button>
              <span style={{ fontSize: 12, color: '#71717A', fontVariantNumeric: 'tabular-nums' }}>
                Organic/Ad Target: <strong>{selectedMonth.organicVsAdSplit}</strong>
              </span>
            </div>
          </div>
          </div>
        </div>

        {/* FULL 12-MONTH RESPONSIVE FINANCIAL & KPI CALENDAR MATRIX */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: 14,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E4E4E7',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}
          >
            <div>
              <h2 id="calendar-matrix-caption" style={{ fontSize: 18, fontWeight: 800, color: '#09090B', margin: 0 }}>
                12-Month Business Forecast, Projected Goals & Actuals Ledger
              </h2>
              <p style={{ fontSize: 12, color: '#71717A', margin: '4px 0 0 0' }}>
                Click any month row to load its visual calendar grid, execution checklist, and actual sales logger above.
              </p>
            </div>
            <span
              style={{
                background: '#FAF6EC',
                color: '#9E782F',
                fontSize: 12,
                fontWeight: 800,
                padding: '6px 12px',
                borderRadius: 999,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              Year-1 Cumulative Profit Goal: +${totals.projProfit.toLocaleString()}
            </span>
          </div>

          <div
            tabIndex={0}
            role="region"
            aria-labelledby="calendar-matrix-caption"
            style={{
              overflowX: 'auto',
              maxInlineSize: '100%'
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  insetBlockStart: 0,
                  zIndex: 2,
                  background: '#F8F8F9'
                }}
              >
                <tr>
                  {[
                    'Month',
                    'Seasonal Driver & Phase',
                    'Active / FBA SKUs',
                    'Expected Orders',
                    'Actual Orders',
                    'Expected Sales ($)',
                    'Actual Sales ($)',
                    'Est. Ad Spend / TACoS',
                    'Expected Net Profit',
                    'Actual Net Profit'
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        textAlign: 'left',
                        padding: '12px 14px',
                        fontSize: 11,
                        fontWeight: 800,
                        color: '#71717A',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #E4E4E7',
                        whiteSpace: 'nowrap',
                        background: '#F8F8F9'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TWELVE_MONTH_ROADMAP.map((m) => {
                  const act = actualsByMonth[m.id];
                  const isSelected = m.id === selectedMonth.id;
                  const rowBg = isSelected ? '#FAF6EC' : '#FFFFFF';
                  return (
                    <tr
                      key={m.id}
                      onClick={() => setSelectedMonthId(m.id)}
                      style={{
                        background: rowBg,
                        cursor: 'pointer'
                      }}
                    >
                      <th
                        scope="row"
                        style={{
                          position: 'sticky',
                          insetInlineStart: 0,
                          zIndex: 1,
                          background: rowBg,
                          padding: '14px',
                          textAlign: 'left',
                          fontWeight: 800,
                          color: '#09090B',
                          borderBottom: '1px solid #E4E4E7',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span style={{ color: '#9E782F', marginRight: 6 }}>
                          {String(m.monthIndex).padStart(2, '0')}
                        </span>
                        {m.monthLabel}
                      </th>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', color: '#3F3F46', minWidth: 220 }}>
                        <div style={{ fontWeight: 700, color: '#09090B' }}>{m.phaseTitle}</div>
                        <div style={{ fontSize: 11, color: '#71717A' }}>{m.organicVsAdSplit}</div>
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', whiteSpace: 'nowrap' }}>
                        <strong>{m.activeSkusGoal}</strong> SKUs ·{' '}
                        <span style={{ color: '#9E782F', fontWeight: 700 }}>{m.fbaHeroSkusGoal} FBA</span>
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 700 }}>
                        {m.expectedMonthlyOrders} <span style={{ fontSize: 11, color: '#71717A' }}>({m.expectedDailyOrders}/d)</span>
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 800, color: act?.actualOrders ? '#09090B' : '#A1A1AA' }}>
                        {act?.actualOrders ? act.actualOrders : '—'}
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 700, color: '#09090B' }}>
                        ${m.expectedGrossRevenue.toLocaleString()}
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 800, color: act?.actualRevenue ? '#09090B' : '#A1A1AA' }}>
                        {act?.actualRevenue ? `$${act.actualRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', whiteSpace: 'nowrap' }}>
                        ${m.expectedAdSpend} <span style={{ fontSize: 11, color: '#71717A' }}>({m.targetTacosPct}% TACoS)</span>
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 800, color: '#15803D' }}>
                        +${m.expectedNetProfit.toLocaleString()}
                      </td>
                      <td style={{ padding: '14px', borderBottom: '1px solid #E4E4E7', fontWeight: 800, color: act?.actualNetProfit ? '#15803D' : '#A1A1AA' }}>
                        {act?.actualNetProfit
                          ? `+$${act.actualNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#09090B', color: '#FFFFFF', fontWeight: 800 }}>
                  <th
                    scope="row"
                    style={{
                      position: 'sticky',
                      insetInlineStart: 0,
                      background: '#09090B',
                      color: '#D9B668',
                      padding: '16px 14px',
                      textAlign: 'left'
                    }}
                  >
                    12-MONTH TOTAL
                  </th>
                  <td style={{ padding: '16px 14px', color: '#D4D4D8' }}>Full Year-1 Scaling Trajectory</td>
                  <td style={{ padding: '16px 14px', color: '#FFFFFF' }}>350 SKUs · 45 FBA</td>
                  <td style={{ padding: '16px 14px', color: '#FFFFFF' }}>{totals.projOrders.toLocaleString()} orders</td>
                  <td style={{ padding: '16px 14px', color: '#D9B668' }}>{totals.actOrders} logged</td>
                  <td style={{ padding: '16px 14px', color: '#FFFFFF' }}>${totals.projRev.toLocaleString()}</td>
                  <td style={{ padding: '16px 14px', color: '#D9B668' }}>
                    ${totals.actRev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '16px 14px', color: '#D4D4D8' }}>${totals.projAd.toLocaleString()} ad budget</td>
                  <td style={{ padding: '16px 14px', color: '#4ADE80' }}>+${totals.projProfit.toLocaleString()}</td>
                  <td style={{ padding: '16px 14px', color: '#4ADE80' }}>
                    +${totals.actProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};
